import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2,
  LayoutDashboard, BarChart3, Workflow, Cpu, Mail, Phone, Database,
  Activity, ArrowRight, Clock, Store
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_CONFIG = {
  ai: { label: 'AI Generation', icon: Cpu, color: 'violet', unit: 'tokens' },
  voice: { label: 'Voice AI', icon: Phone, color: 'blue', unit: 'minutes' },
  email: { label: 'Email', icon: Mail, color: 'amber', unit: 'sends' },
  crm: { label: 'Outbound Leads', icon: Users, color: 'emerald', unit: 'operations' },
  memory: { label: 'Memory', icon: Database, color: 'cyan', unit: 'operations' },
};

const HubDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usageRes, emailRes] = await Promise.all([
          axios.get(`${API}/hub/usage`),
          axios.get(`${API}/hub/email/stats`),
        ]);
        setUsage(usageRes.data);
        setEmailStats(emailRes.data);
      } catch (err) {
        console.error('Failed to fetch hub data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5" />{item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="hub-title">Master API Hub</h1>
            </div>
            <span className="text-xs font-mono px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-testid="total-calls">
              {usage?.total_api_calls || 0} API CALLS
            </span>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : (
            <>
              {/* Info banner */}
              <div className="bg-[#0a0a0a] border border-white/5 p-5">
                <h2 className="text-base font-bold mb-1">Unified API Layer</h2>
                <p className="text-sm text-muted-foreground">All external providers abstracted into internal endpoints. AI, Voice, Email, CRM, and Memory — metered and logged.</p>
              </div>

              {/* Service Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="service-cards">
                {Object.entries(SERVICE_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const serviceData = usage?.services?.[key] || { calls: 0, units: 0 };
                  const colorMap = {
                    violet: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
                    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
                    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
                    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
                    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
                  };
                  const cls = colorMap[config.color] || colorMap.emerald;
                  return (
                    <div key={key} className={`bg-[#0a0a0a] border p-4 ${cls.split(' ').slice(2).join(' ') || 'border-white/5'}`} data-testid={`service-${key}`}>
                      <div className={`w-9 h-9 flex items-center justify-center mb-3 ${cls.split(' ').slice(0, 2).join(' ')}`}>
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{config.label}</p>
                      <p className="font-mono text-2xl font-bold text-white">{serviceData.calls}</p>
                      <p className="text-[10px] text-muted-foreground">{serviceData.units.toLocaleString()} {config.unit}</p>
                    </div>
                  );
                })}
              </div>

              {/* Email Stats */}
              {emailStats && (
                <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="email-stats">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Delivery
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div><p className="text-[10px] text-muted-foreground uppercase">Total</p><p className="font-mono text-lg font-bold">{emailStats.total}</p></div>
                    <div><p className="text-[10px] text-muted-foreground uppercase">Sent</p><p className="font-mono text-lg font-bold text-emerald-400">{emailStats.sent}</p></div>
                    <div><p className="text-[10px] text-muted-foreground uppercase">Failed</p><p className="font-mono text-lg font-bold text-red-400">{emailStats.failed}</p></div>
                    <div><p className="text-[10px] text-muted-foreground uppercase">Bounce Rate</p><p className="font-mono text-lg font-bold text-amber-400">{emailStats.bounce_rate}%</p></div>
                  </div>
                </div>
              )}

              {/* API Endpoints Reference */}
              <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="api-reference">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">API Endpoints</h3>
                <div className="space-y-2 font-mono text-sm">
                  {[
                    { method: 'POST', path: '/api/hub/ai/generate', desc: 'AI text generation with provider fallback' },
                    { method: 'POST', path: '/api/hub/voice/call', desc: 'Text-to-speech voice generation' },
                    { method: 'POST', path: '/api/hub/email/send', desc: 'Send email via abstracted provider' },
                    { method: 'POST', path: '/api/hub/crm/createLead', desc: 'Create CRM lead with adapter pattern' },
                    { method: 'POST', path: '/api/hub/memory/store', desc: 'Store key-value data' },
                    { method: 'POST', path: '/api/hub/memory/query', desc: 'Query stored memory' },
                    { method: 'GET', path: '/api/hub/usage', desc: 'Get usage metrics' },
                    { method: 'GET', path: '/api/hub/email/stats', desc: 'Email delivery stats' },
                  ].map((ep, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold ${ep.method === 'POST' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {ep.method}
                      </span>
                      <span className="text-white text-xs">{ep.path}</span>
                      <span className="text-xs text-muted-foreground ml-auto hidden sm:block">{ep.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              {usage?.recent_activity?.length > 0 && (
                <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="recent-activity">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Recent Activity
                  </h3>
                  <div className="space-y-2">
                    {usage.recent_activity.slice(0, 10).map((act, i) => {
                      const config = SERVICE_CONFIG[act.service] || { label: act.service, color: 'white' };
                      return (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0 text-xs">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 font-mono uppercase text-[10px] ${
                              config.color === 'violet' ? 'bg-violet-500/20 text-violet-400' :
                              config.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                              config.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                              config.color === 'cyan' ? 'bg-cyan-500/20 text-cyan-400' :
                              'bg-emerald-500/20 text-emerald-400'
                            }`}>{act.service}</span>
                            <span className="text-muted-foreground">{act.operation}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-muted-foreground">{act.units} units</span>
                            <span className="text-muted-foreground">{new Date(act.created_at).toLocaleTimeString()}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HubDashboard;
